import { ComponentProps, FC } from "react";
import styled, { css } from "styled-components";
import { PrimaryButton } from "./PrimaryButton";
import { link, linkDisabled } from "../../typography/alpha";
import { Icon, IconSize } from "../Icon";
import { IconVariant } from "../Icon/types";
import { ButtonVariant } from "./types";

type TextButtonProps = ComponentProps<typeof PrimaryButton> & {
  iconSize?: IconSize;
};

const StyledButton = styled.button<TextButtonProps>`
  ${link.body3}
  ${({ variant }) => {
    if (variant === ButtonVariant.DANGER) {
      return css`
        color: var(--color-error-600);
        text-decoration: none;
        &:visited {
          color: var(--color-error-400);
        }
        &:hover {
          color: var(--color-error-500);
          svg {
            --main-color: var(--color-error-500);
          }
        }
      `;
    } else {
      return css`
        &:hover {
          svg {
            --main-color: var(--color-secondary-600);
          }
        }
      `;
    }
  }}

  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  margin: 0;
  padding: 0;

  cursor: pointer;
  &:hover {
    text-decoration-line: underline;
  }
  &:disabled {
    ${linkDisabled.body3}
    cursor: not-allowed;
    svg {
      --main-color: var(--color-secondary-300);
    }
    &:hover {
      svg {
        --main-color: var(--color-secondary-300);
      }
    }
  }
  > *:not(:last-child) {
    margin-right: 4px;
  }
`;

const iconVariantByButtonVariant = {
  DISABLED: ButtonVariant.DISABLED,
  DANGER: ButtonVariant.DANGER,
  DEFAULT: "ACTIVE" as IconVariant,
};

export const TextButton: FC<TextButtonProps> = ({
  children,
  iconName,
  iconSize = "small",
  disabled = false,
  variant = ButtonVariant.DEFAULT,
  ...buttonProps
}) => {
  return (
    <StyledButton
      {...buttonProps}
      disabled={disabled || variant === ButtonVariant.DISABLED}
      variant={variant}
    >
      {iconName && (
        <Icon
          iconName={iconName}
          iconSize={iconSize}
          variant={
            disabled
              ? ButtonVariant.DISABLED
              : iconVariantByButtonVariant[variant]
          }
          plain
        />
      )}
      <span>{children}</span>
    </StyledButton>
  );
};
